import React, { useState, useContext } from 'react';
import { enquiryService } from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import {
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Grid
} from '@mui/material';

import '../styles/_utilities.css';

export default function EnquiryForm({ onEnquiryCreated }) {
  const { user } = useContext(AuthContext);
  const [clientName, setClientName] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [otherLeadSource, setOtherLeadSource] = useState(''); // New State for custom source

  const [items, setItems] = useState([
    {
      name: '', description: '', quantity: '', budgetPerUnit: '',
      deliveryLocation: '', targetDateOfDelivery: '', selectedFiles: []
    }
  ]);
  const [enquiryFiles, setEnquiryFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    const newItems = [...items];
    let processedValue = value;

    if (name === 'quantity' || name === 'budgetPerUnit') {
      if (name === 'budgetPerUnit') {
        const parts = value.split('.');
        processedValue = parts[0].replace(/[^\d]/g, '');
        if (parts.length > 1) {
          processedValue += '.' + parts[1].replace(/[^\d]/g, '').substring(0, 2);
        }
      } else {
        processedValue = value.replace(/[^\d]/g, '');
      }
    }
    newItems[index][name] = processedValue;
    setItems(newItems);
  };

  const handleEnquiryFilesChange = (e) => {
    setEnquiryFiles(Array.from(e.target.files));
  };

  const handleItemFilesChange = (index, e) => {
    const newItems = [...items];
    newItems[index].selectedFiles = Array.from(e.target.files);
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, {
      name: '', description: '', quantity: '', budgetPerUnit: '',
      deliveryLocation: '', targetDateOfDelivery: '', selectedFiles: []
    }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) return;
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!clientName.trim()) {
      setError("Please enter a client name.");
      return;
    }

    // Lead Source Validation & Selection
    let finalLeadSource = leadSource;
    if (!leadSource) {
      setError("Please select a lead source.");
      return;
    }
    if (leadSource === 'Others') {
      if (!otherLeadSource.trim()) {
        setError("Please specify the other lead source.");
        return;
      }
      finalLeadSource = otherLeadSource.trim(); // Use the custom text
    }

    if (items.some(item => !item.name.trim() || !item.description.trim())) {
        setError("Please ensure every item has at least a name and description.");
        return;
    }

    setIsSubmitting(true);

    const itemsForSubmission = items.map(item => ({
      ...item,
      quantity: item.quantity ? parseInt(item.quantity, 10) : null,
      budgetPerUnit: item.budgetPerUnit ? parseFloat(item.budgetPerUnit) : null,
      selectedFiles: undefined,
      tempAttachedFileNames: item.selectedFiles.map(file => file.name)
    }));

    const enquiryData = {
      clientName,
      leadSource: finalLeadSource, // Send the resolved source
      requestedBy: user.username,
      items: itemsForSubmission,
    };

    const formData = new FormData();
    formData.append('enquiry', new Blob([JSON.stringify(enquiryData)], { type: 'application/json' }));

    enquiryFiles.forEach(file => {
        formData.append(`files`, file);
    });

    items.forEach(item => {
        item.selectedFiles.forEach(file => {
            formData.append(`files`, file);
        });
    });

    try {
        await enquiryService.post("/enquiry/with-files", formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        setMessage('Enquiry submitted successfully!');

        // Reset form
        setClientName('');
        setLeadSource('');
        setOtherLeadSource(''); // Reset custom source
        setItems([
            {
                name: '', description: '', quantity: '', budgetPerUnit: '',
                deliveryLocation: '', targetDateOfDelivery: '', selectedFiles: []
            }
        ]);
        setEnquiryFiles([]);

        if (onEnquiryCreated) {
            onEnquiryCreated();
        }
    } catch (err) {
      console.error("Enquiry submission error:", err.response || err);
      setError(`Error submitting enquiry: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="form-card" sx={{ maxWidth: '800px', mx: 'auto' }}>
      <Typography variant="h2" component="h2" sx={{ textAlign: 'center', mb: 3 }}>
        Create New Enquiry
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          id="clientName"
          label="Client Name*"
          placeholder="Enter client name"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          required
          margin="normal"
          variant="outlined"
        />

        {/* Lead Source Section */}
        <Grid container spacing={2}>
            <Grid item xs={leadSource === 'Others' ? 6 : 12}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="lead-source-label">Lead Source</InputLabel>
                  <Select
                    labelId="lead-source-label"
                    id="leadSource"
                    value={leadSource}
                    label="Lead Source"
                    onChange={(e) => setLeadSource(e.target.value)}
                  >
                    <MenuItem value="Self">Self</MenuItem>
                    <MenuItem value="Digital">Digital</MenuItem>
                    <MenuItem value="Referral">Referral</MenuItem>
                    <MenuItem value="CE Worldwide">CE Worldwide</MenuItem>
                    <Divider />
                    <MenuItem value="Others">Others</MenuItem>
                  </Select>
                </FormControl>
            </Grid>
            {/* Conditionally Render Custom Text Field */}
            {leadSource === 'Others' && (
                <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="otherLeadSource"
                      label="Please specify source*"
                      value={otherLeadSource}
                      onChange={(e) => setOtherLeadSource(e.target.value)}
                      required
                      margin="normal"
                      variant="outlined"
                      // Remove top margin to align better with Select
                      sx={{ mt: 2, mb: 1 }}
                    />
                </Grid>
            )}
        </Grid>

        <FormControl fullWidth margin="normal">
          <label htmlFor="enquiry-file-button">
            <input
              style={{ display: 'none' }}
              id="enquiry-file-button"
              type="file"
              multiple
              onChange={handleEnquiryFilesChange}
              accept="image/*,application/pdf"
            />
            <Button
              variant="outlined"
              component="span"
              fullWidth
            >
              {enquiryFiles.length > 0 ? `${enquiryFiles.length} file(s) selected for Enquiry` : 'Attach Files to Enquiry (Optional, Max 10)'}
            </Button>
            {enquiryFiles.length > 0 && (
                <Box sx={{ mt: 1, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Selected Enquiry Files:</Typography>
                    {enquiryFiles.map((file, index) => (
                        <Typography key={index} variant="caption" display="block">{file.name}</Typography>
                    ))}
                </Box>
            )}
          </label>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {items.map((item, index) => (
          <Box key={index} className="enquiry-item-subform" sx={{ mb: 3 }}>
            <Typography variant="h4" component="h4" sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
              Product {index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="name"
                  label="Product Name*"
                  value={item.name}
                  onChange={e => handleInputChange(index, e)}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="quantity"
                  label="Quantity"
                  type="text"
                  value={item.quantity}
                  onChange={e => handleInputChange(index, e)}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  inputProps={{ pattern: "[0-9]*" }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="budgetPerUnit"
                  label="Budget Per Unit"
                  type="text"
                  value={item.budgetPerUnit}
                  onChange={e => handleInputChange(index, e)}
                  onKeyPress={(event) => {
                    if (!/[0-9.]/.test(event.key) || (event.key === '.' && event.target.value.includes('.'))) {
                      event.preventDefault();
                    }
                  }}
                  inputProps={{ pattern: "[0-9]*(\\.[0-9]{0,2})?" }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="deliveryLocation"
                  label="Delivery Location"
                  value={item.deliveryLocation}
                  onChange={e => handleInputChange(index, e)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="targetDateOfDelivery"
                  label="Target Delivery Date"
                  type="date"
                  value={item.targetDateOfDelivery}
                  onChange={e => handleInputChange(index, e)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="Product Description*"
                  value={item.description}
                  onChange={e => handleInputChange(index, e)}
                  required
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <label htmlFor={`item-file-button-${index}`}>
                    <input
                      style={{ display: 'none' }}
                      id={`item-file-button-${index}`}
                      type="file"
                      multiple
                      onChange={(e) => handleItemFilesChange(index, e)}
                      accept="image/*,application/pdf"
                    />
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                    >
                      {item.selectedFiles.length > 0 ? `${item.selectedFiles.length} file(s) selected for Product ${index + 1}` : `Attach Files to Product ${index + 1} (Optional, Max 10)`}
                    </Button>
                    {item.selectedFiles.length > 0 && (
                        <Box sx={{ mt: 1, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>Selected Files for Product {index + 1}:</Typography>
                            {item.selectedFiles.map((file, fileIndex) => (
                                <Typography key={fileIndex} variant="caption" display="block">{file.name}</Typography>
                            ))}
                        </Box>
                    )}
                  </label>
                </FormControl>
              </Grid>
            </Grid>
            {items.length > 1 && (
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={() => handleRemoveItem(index)}
                sx={{ mt: 2 }}
              >
                Remove This Product
              </Button>
            )}
            <Divider sx={{ my: 3 }} />
          </Box>
        ))}

        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={handleAddItem}
          sx={{ mt: 2, mb: 2 }}
        >
          + Add Another Product
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={isSubmitting}
          sx={{ mt: 2 }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
        </Button>

        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
      </form>
    </Box>
  );
}